import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import HomeIcon from '@mui/icons-material/Home';
import CategoryIcon from '@mui/icons-material/Category';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/Group';
import RedeemIcon from '@mui/icons-material/Redeem';

import NavbarBack from './NavbarBack';
import ProductList from './ProductList';
import CategoryPage from './CategorySelector';
import SearchById from './SearchById';
import ResendOfferList from './ResendOfferList';
import FollowingId from './FollowingId';

const pages = ['Homepage', 'Categories', 'SearchById', 'Following', 'Resend Offers'];

const pageIcons = {
  Homepage: <HomeIcon fontSize="medium" />,
  Categories: <CategoryIcon fontSize="medium" />,
  SearchById: <SearchIcon fontSize="medium" />,
  Following: <GroupIcon fontSize="medium" />,
  'Resend Offers': <RedeemIcon fontSize="medium" sx={{ color: 'gold' }} />,
};

const UserSidebarLayout = () => {
  const [selectedPage, setSelectedPage] = useState('Homepage');
  const [filters, setFilters] = useState({ category: 'All', subCategory: 'All' });
  const [showSidebar, setShowSidebar] = useState(true);

  const location = useLocation();
  const shopName = location.state?.shopName || 'All';

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleCategorySelect = (category, subCategory) => {
    setFilters({ category, subCategory });
    setSelectedPage('Homepage');
  };

  const resetFilters = () => {
    setFilters({ category: 'All', subCategory: 'All' });
  };

  const renderContent = () => {
    switch (selectedPage) {
      case 'Homepage':
        return (
          <ProductList
            shopName={shopName}
            category={filters.category}
            subCategory={filters.subCategory}
            onResetFilters={resetFilters}
          />
        );
      case 'Categories':
        return <CategoryPage onCategorySelect={handleCategorySelect} />;
      case 'SearchById':
        return <SearchById shopName={shopName} />;
      case 'Following':
        return <FollowingId />;
      case 'Resend Offers':
        return <ResendOfferList />;
      default:
        return <ProductList shopName={shopName} />;
    }
  };

  return (
    <>
      <NavbarBack
        title={`${shopName !== 'All' ? shopName : 'Platform'}`}
        onToggleSidebar={!isMobile ? () => setShowSidebar(prev => !prev) : undefined}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column-reverse' : 'row',
          minHeight: '90vh',
          bgcolor: '#fff',
        }}
      >
        {/* Sidebar */}
    <Box
  sx={{
    display: showSidebar || isMobile ? 'flex' : 'none',
    position: 'fixed',
    top: isMobile ? 'auto' : 64,
    bottom: isMobile ? 0 : 'auto',
    left: 0,
    width: isMobile ? '100%' : '150px',
    height: isMobile ? 70 : '100vh',
    zIndex: 1000,
    bgcolor: '#fff',
    borderTop: isMobile ? '1px solid #ddd' : 'none',
    borderRight: !isMobile ? '1px solid #ddd' : 'none',
    transition: 'all 0.3s ease',
  }}
>
  <List
    sx={{
      display: 'flex',
      flexDirection: isMobile ? 'row' : 'column',
      justifyContent: isMobile ? 'space-around' : 'flex-start',
      alignItems: 'center',
      height: '100%',
      p: 0,
    }}
  >
    {pages.map((page) => (
      <ListItem key={page} disablePadding sx={{ width: '100%' }}>
        <ListItemButton
          selected={selectedPage === page}
          onClick={() => {
            setSelectedPage(page);
            if (page === 'Homepage') resetFilters();
          }}
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: isMobile ? 'center' : 'flex-start',
            px: 1.5,
            py: isMobile ? 0.5 : 2,
            minHeight: isMobile ? 60 : 36,
            gap: isMobile ? 0 : 2.5,
          }}
        >
          <Box>{pageIcons[page]}</Box>
          <ListItemText
            primary={page}
            primaryTypographyProps={{
              fontSize: isMobile ? 10 : 15,
              textAlign: isMobile ? 'center' : 'left',
            }}
          />
        </ListItemButton>
      </ListItem>
    ))}
  </List>
</Box>


        {/* Main Content */}
        <Box
          sx={{
            flexGrow: 1,
            p: 1,
            overflowY: 'auto',
            ml: !isMobile ? (showSidebar ? '140px' : '0') : 0,
            mb: isMobile ? '80px' : 0,
            transition: 'margin 0.3s ease',
          }}
        >
          {renderContent()}
        </Box>
      </Box>
    </>
  );
};

export default UserSidebarLayout;
